import { useState } from "react";
import { MapPin, ArrowUpRight } from "lucide-react";

const destinations = [
  {
    name: "Udaipur",
    region: "Rajasthan, India",
    tagline: "The City of Lakes",
    blurb:
      "Palace weddings inside the Taj Lake Palace, Leela Udaipur and Oberoi Udaivilas — sun-drenched mandaps, royal Rajput courtyards and processions on water.",
    image:
      "https://images.unsplash.com/photo-1599661046827-dacde6976549?auto=format&fit=crop&w=1200&q=80",
    span: "tall",
  },
  {
    name: "Goa",
    region: "Western Coast, India",
    tagline: "Sun, Sea & Susegad",
    blurb:
      "Beach-side destination weddings at Taj Exotica, W Goa and Grand Hyatt — Portuguese arches, palm-fringed mandaps and after-parties under a Konkan sky.",
    image:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
  {
    name: "Jaipur",
    region: "Rajasthan, India",
    tagline: "The Pink City",
    blurb:
      "Royal Rajputana weddings at Rambagh Palace, Hyatt Regency Mansarovar and Fairmont — gold-leaf havelis, elephant baraats and havelis crowned with marigold.",
    image:
      "https://images.unsplash.com/photo-1477587458883-47145ed94245?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
  {
    name: "Mumbai",
    region: "Maharashtra, India",
    tagline: "Editorial · Modern · Iconic",
    blurb:
      "City weddings at the Taj Mahal Palace, Trident, Four Seasons, St Regis and JW Marriott — couture sangeets, art-deco receptions and skyline cocktails.",
    image:
      "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?auto=format&fit=crop&w=1200&q=80",
    span: "tall",
  },
  {
    name: "Tuscany",
    region: "Italy",
    tagline: "Old-World Romance",
    blurb:
      "Vineyard weddings in Chianti and Val d'Orcia — cypress-lined receptions at Borgo Santo Pietro and Castello di Velona, slow dinners under olive trees.",
    image:
      "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
  {
    name: "Maldives",
    region: "Indian Ocean",
    tagline: "Barefoot Luxury",
    blurb:
      "Overwater chapel weddings and private-island celebrations at Soneva Fushi, Cheval Blanc Randheli and Velaa — turquoise vows and starlit beach receptions.",
    image:
      "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
  {
    name: "Bangalore",
    region: "Karnataka, India",
    tagline: "Garden City Elegance",
    blurb:
      "Boutique weddings at the Leela Palace, ITC Gardenia and Taj West End — manicured lawns, art-deco ballrooms and intimate poolside receptions in a forever-pleasant climate.",
    image:
      "https://images.unsplash.com/photo-1596402184320-417e7178b2cd?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
  {
    name: "Dubai",
    region: "United Arab Emirates",
    tagline: "Skyline · Desert · Spectacle",
    blurb:
      "Glamorous weddings at the Burj Al Arab, Atlantis The Royal and One&Only Royal Mirage — desert sundowners, skyline cocktails and fireworks above the Arabian Gulf.",
    image:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1200&q=80",
    span: "tall",
  },
  {
    name: "Thailand",
    region: "Phuket · Krabi · Koh Samui",
    tagline: "Boutique Island Glamour",
    blurb:
      "Private beach weddings at Trisara, Aman Phuket and Six Senses Yao Noi — Thai longtail baraats, lantern-lit mandaps and Andaman Sea backdrops.",
    image:
      "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=1200&q=80",
    span: "wide",
  },
];

const Destinations = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0B0F18]"
    >
      <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
        <div className="lg:col-span-5 reveal">
          <div className="overline mb-6">Destinations</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
            Where shall we
            <br />
            <span className="italic text-[#B8935C]">say I do</span>?
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 lg:pt-6 reveal">
          <p className="text-[#F0EBE1]/70 text-base sm:text-lg leading-relaxed font-light max-w-lg">
            From the lake palaces of Udaipur to the cliffs of Bali, the
            vineyards of Tuscany and the overwater chapels of the Maldives — we
            plan luxury destination weddings across India and the world. A
            curated edit of our most loved geographies.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[260px] md:auto-rows-[320px] gap-3 md:gap-5">
        {destinations.map((d, i) => {
          const tall = d.span === "tall";
          return (
            <a
              key={d.name}
              href="#contact"
              data-testid={`destination-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`group relative overflow-hidden cursor-pointer ${
                tall ? "row-span-2" : ""
              }`}
            >
              <img
                src={d.image}
                alt={`Luxury ${d.name} destination wedding planning by Bon Evento`}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://customer-assets.emergentagent.com/job_bespoke-events-3/artifacts/fvgw56fc_DSC05097.jpg";
                }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]"
              />
              <div
                className={`absolute inset-0 transition-opacity duration-700 ${
                  hovered === i
                    ? "bg-gradient-to-t from-[#080B12] via-[#080B12]/65 to-[#080B12]/20"
                    : "bg-gradient-to-t from-[#080B12] via-[#080B12]/45 to-transparent"
                }`}
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-transparent group-hover:ring-[#B8935C]/40 transition-all duration-500" />

              {/* Top: small location chip */}
              <div className="absolute top-4 left-4 md:top-5 md:left-5 flex items-center gap-1.5 text-[#B8935C]/80 group-hover:text-[#B8935C] transition-colors">
                <MapPin size={11} strokeWidth={1.4} />
                <span className="text-[9px] md:text-[10px] uppercase tracking-[0.24em] font-light">
                  {d.region}
                </span>
              </div>

              {/* Bottom: name + tagline + hover blurb */}
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="flex items-end justify-between gap-3 mb-1">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#F0EBE1] leading-tight">
                    {d.name}
                  </h3>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.3}
                    className="text-[#F0EBE1]/55 group-hover:text-[#B8935C] -translate-y-0.5 -translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-500 flex-shrink-0"
                  />
                </div>
                <div className="overline text-[9px] md:text-[10px] text-[#B8935C]/85 mb-3">
                  {d.tagline}
                </div>
                <p
                  className={`text-[#F0EBE1]/70 text-xs md:text-[13px] leading-relaxed font-light max-w-md transition-all duration-700 ease-out ${
                    hovered === i || tall
                      ? "opacity-100 max-h-40"
                      : "opacity-0 max-h-0 lg:opacity-0 lg:max-h-0"
                  }`}
                >
                  {d.blurb}
                </p>
              </div>
            </a>
          );
        })}
      </div>

      <div className="mt-16 text-center reveal">
        <p className="text-[#F0EBE1]/55 text-sm font-light italic max-w-2xl mx-auto leading-relaxed">
          Dreaming of a wedding somewhere unexpected? Tell us where — Santorini,
          Kyoto, Marrakech, the Amalfi Coast — and we'll bring it to life.
        </p>
        <a
          href="#contact"
          data-testid="destinations-cta"
          className="mt-8 inline-flex items-center gap-3 px-9 py-4 border border-[#B8935C] text-[#B8935C] text-xs uppercase tracking-[0.28em] hover:bg-[#B8935C] hover:text-[#080B12] transition-all duration-500"
        >
          Plan Your Destination Wedding
        </a>
      </div>
    </section>
  );
};

export default Destinations;
